import { useState } from 'react';

import { useOrders, useConfig } from '@/hooks/useApi';
import { formatCurrency, formatNumber, formatPrice } from '@/lib/format';
import type { Order } from '@/types';

const ITEMS_PER_PAGE = 25;

export function Orders() {
	const { data: config } = useConfig();
	const activeSymbols = config?.activeSymbols || [];

	const [selectedSymbol, setSelectedSymbol] = useState<string>();
	const [page, setPage] = useState(1);

	const { data: ordersData, isLoading } = useOrders(page, ITEMS_PER_PAGE, selectedSymbol);
	const orders: Order[] = ordersData?.orders ?? [];
	const total = ordersData?.total ?? 0;
	const totalPages = ordersData?.pagination?.totalPages ?? Math.ceil(total / ITEMS_PER_PAGE);
	const hasNext = ordersData?.pagination?.hasNext ?? page < totalPages;
	const hasPrev = ordersData?.pagination?.hasPrev ?? page > 1;

	// Handle symbol filter
	const handleSymbolChange = (symbol: string) => {
		setSelectedSymbol(symbol || undefined);
		setPage(1);
	};

	// Handle page navigation
	const handlePrev = () => {
		if (hasPrev) {
			setPage((p) => p - 1);
		}
	};

	const handleNext = () => {
		if (hasNext) {
			setPage((p) => p + 1);
		}
	};

	// Format timestamp
	const formatTime = (timestamp: number) => {
		return new Date(timestamp).toLocaleString();
	};

	// Pagination component
	const Pagination = () => (
		<div className="px-4 py-3 bg-surface-hover flex items-center justify-between">
			<button
				onClick={handlePrev}
				disabled={!hasPrev}
				className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-surface hover:bg-surface-hover text-text"
			>
				Previous
			</button>
			<span className="text-sm text-text-muted">
				Page {page} {totalPages > 0 && `of ${totalPages}`}
			</span>
			<button
				onClick={handleNext}
				disabled={!hasNext}
				className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-surface hover:bg-surface-hover text-text"
			>
				Next
			</button>
		</div>
	);

	// Format symbol for display
	const formatSymbol = (symbol: string) => {
		return symbol.replace('PERP_', '').replace('_USDC', '');
	};

	return (
		<div className="space-y-6">
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-text">Orders</h1>
					<p className="text-text-muted mt-1">Filled order history from the last 30 days</p>
				</div>
			</div>

			{/* Symbol Filter */}
			{activeSymbols.length > 0 && (
				<div className="card">
					<span className="text-sm font-medium text-text">Filter by Symbol</span>
					<select
						value={selectedSymbol || ''}
						onChange={(e) => handleSymbolChange(e.target.value)}
						className="mt-2 w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-primary"
					>
						<option value="">All Symbols</option>
						{activeSymbols.map((symbol) => (
							<option key={symbol} value={symbol}>
								{formatSymbol(symbol)}
							</option>
						))}
					</select>
				</div>
			)}

			{isLoading ? (
				<div className="flex items-center justify-center h-64">
					<div className="text-text-muted">Loading orders...</div>
				</div>
			) : orders.length === 0 ? (
				<div className="card text-center py-12">
					<p className="text-text-muted">No orders found</p>
				</div>
			) : (
				<div className="card overflow-hidden">
					{/* Top pagination */}
					<Pagination />

					<div className="px-4 py-3 border-y border-border bg-surface-hover">
						<p className="text-sm text-text-muted">
							Showing {orders.length} of {total} orders
						</p>
					</div>

					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="border-b border-border">
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">Time</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
										Symbol
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">Side</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
										Filled
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
										Amount
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
										Avg Price
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
										Realized PnL
									</th>
									<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">Fee</th>
								</tr>
							</thead>
							<tbody>
								{orders.map((order: Order) => (
									<tr key={order.orderId} className="border-b border-border last:border-b-0">
										<td className="py-3 px-4 text-text-muted text-sm">
											{formatTime(order.createdAt)}
										</td>
										<td className="py-3 px-4 text-text font-medium">
											{formatSymbol(order.symbol)}
										</td>
										<td className="py-3 px-4">
											<span
												className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
													order.side === 'BUY'
														? 'bg-success/10 text-success'
														: 'bg-danger/10 text-danger'
												}`}
											>
												{order.side}
											</span>
										</td>
										<td className="py-3 px-4 text-text">{formatNumber(order.filledSize, 4)}</td>
										<td className="py-3 px-4 text-text">
											{order.avgPrice ? formatCurrency(order.filledSize * order.avgPrice, 2) : '-'}
										</td>
										<td className="py-3 px-4 text-text">
											{order.avgPrice ? formatPrice(order.avgPrice) : '-'}
										</td>
										<td
											className={`py-3 px-4 font-medium ${
												order.realizedPnl > 0
													? 'text-success'
													: order.realizedPnl < 0
														? 'text-danger'
														: 'text-text-muted'
											}`}
										>
											{formatCurrency(order.realizedPnl, 2)}
										</td>
										<td className="py-3 px-4 text-text-muted">
											{formatCurrency(order.totalFee, 4)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Bottom pagination */}
					<div className="border-t border-border">
						<Pagination />
					</div>
				</div>
			)}
		</div>
	);
}
